import { cloneDeep, set } from 'lodash';
import * as yup from 'yup';

/** Possible types for the `value` of a `FormField` */
type FieldValue = string | boolean;

/** A form field with a value of type V */
export interface FormField<V = FieldValue> {
  /** Error message or null */
  error: string | null;
  /** Initial value when the form was loaded */
  initialValue: V;
  /** Path to the property in the resource object */
  propertyPath: string;
  /** Whether the field is read-only */
  readOnly: boolean;
  /** Whether the value should be parsed (e.g., JSON.parse) before being set in the resource */
  shouldParse: boolean;
  /** Current value of the field */
  value: V;
}

/** A form with one to many fields */
export interface Form {
  [key: string]: FormField<FieldValue>;
}

/** Returns true if any form field has an empty value */
export const doesFormHaveEmptyValue = (form: Form) => {
  return Object.values(form).some((field) => field.value === '');
};

/** Returns true if all form fields have their initial values */
export const doesFormHaveInitialValues = (form: Form) => {
  return Object.values(form).every((field) => field.value === field.initialValue);
};

/** Returns true if any form field has an error */
export const doesFormHaveError = (form: Form) => {
  return Object.values(form).some((field) => field.error !== null);
};

/**
 * Updates the `value` of a field in a form and resets any error.
 *
 * @param value - The new value of the form field
 * @param key - The key of the form field being updated
 * @param setForm - The state setter function for the form
 */
export const onValueChange = <F extends Form>(
  value: FieldValue,
  key: keyof F,
  setForm: React.Dispatch<React.SetStateAction<F>>
) => {
  setForm((prev) => ({
    ...prev,
    [key]: { ...prev[key], error: null, value }
  }));
};

/**
 * Handles the onBlur event for an input field.
 * It validates the field's value against the provided schema
 * and updates the error message if validation fails.
 * The schema should be a yup object schema that validates
 * an object with a single key corresponding to the form field.
 *
 * @param e - The blur event from the input field
 * @param key - The key of the form field being validated
 * @param objectSchema - The yup object schema used for validation
 * @param setForm - The state setter function for the form
 */
export const onInputBlur = async <F extends Form, OS extends object>(
  e: React.ChangeEvent<HTMLInputElement>,
  key: keyof F,
  objectSchema: yup.ObjectSchema<OS>,
  setForm: React.Dispatch<React.SetStateAction<F>>
) => {
  e.preventDefault();

  let invalidMessage: string | null = null;
  const trimmedValue = e.target.value.trim();
  if (trimmedValue.length) {
    const result = await objectSchema.validate({ [key]: trimmedValue }).catch((err) => err);
    invalidMessage = (result?.message as string) || null;
  }

  setForm((prev) => ({
    ...prev,
    [key]: { ...prev[key], error: invalidMessage, value: trimmedValue }
  }));
};

/**
 * Updates the given resource object with the values from the form.
 * Only fields that are not read-only and have values different from
 * their initial values will be updated. If a field has `shouldParse`
 * set to true, its value will be parsed (e.g., using JSON.parse)
 * before being set in the resource.
 *
 * @param resource - The original resource object to be updated
 * @param form - The form containing the updated values
 * @returns A new resource object with the updated values from the form
 */
export const getUpdatedResourceWithFormValues = <R extends object>(resource: R, form: Form): R => {
  const tempResource = cloneDeep(resource);

  Object.values(form).forEach((field) => {
    const { readOnly, initialValue, value, propertyPath, shouldParse } = field;

    if (!readOnly && value !== initialValue) {
      const parsedValue = shouldParse && typeof value === 'string' ? JSON.parse(value) : value;
      set(tempResource, propertyPath, parsedValue);
    }
  });

  return tempResource;
};
